import { useContext, useEffect, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { Button, Checkbox, IconButton } from "rsuite";
import { LoadingContext } from "../contexts/LoadingContext";
import { SearchContext } from "../contexts/SearchContext";
import {
  createMerchantType,
  deleteMerchantType,
  getMerchantTypes,
  updateMerchantType,
} from "../services/sales/MerchantTypeApi";
import { errorToast } from "../utils/helper-ui";
import { PaginationResponse } from "../objects/pagination";
import { getPagination } from "../utils/helper";
import { MerchantType } from "../models/sales/merchant_type";
import { Table, Pagination } from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import { BsTrash3 } from "react-icons/bs";
import MerchantTypeForm from "../components/features/sales/MerchantTypeForm";
const { Column, HeaderCell, Cell } = Table;
interface MerchantTypePageProps {}

const MerchantTypePage: FC<MerchantTypePageProps> = ({}) => {
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(30);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [merchantTypes, setMerchantTypes] = useState<MerchantType[]>([]);
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const [selectedMerchantType, setSelectedMerchantType] =
    useState<MerchantType>();

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === merchantTypes.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (
    checkedKeys.length > 0 &&
    checkedKeys.length < merchantTypes.length
  ) {
    indeterminate = true;
  }

  const handleCheckAll = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? merchantTypes.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getMerchantTypes({ page, size, search });
      const resJson = await res.json();
      //   console.log(data);
      setMerchantTypes((prev) => [...prev, ...resJson.data.items]);
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const removeMerchantType = async (merchantType: MerchantType) => {
    try {
      setIsLoading(true);
      if (
        window.confirm(
          `Are you sure you want to delete merchantType with name ${merchantType.name}?`
        )
      ) {
        await deleteMerchantType(merchantType.id!);
        fetchData();
      }
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const saveMerchantType = async (merchantType: MerchantType) => {
    try {
      setIsLoading(true);
      if (selectedMerchantType) {
        await updateMerchantType(selectedMerchantType.id!, merchantType);
      } else {
        await createMerchantType(merchantType);
      }
      setModalFormOpen(false);
      setSelectedMerchantType(undefined);
      fetchData();
      setMerchantTypes([]);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Merchant Type</h1>
      {/* button */}
      <div>
        <IconButton
          size="sm"
          appearance="primary"
          color="green"
          icon={<PlusIcon />}
          onClick={() => setModalFormOpen(true)}
        >
          Create Merchant Type
        </IconButton>
      </div>
    </div>
  );
  return (
    <MainLayout header={renderHeader()}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th
                className="px-4 py-2 text-left border bg-gray-50"
                style={{ width: 100 }}
              >
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                />
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Name</th>
              <th className="px-4 py-2 text-left border bg-gray-50">
                Description
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Icon</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Action</th>
            </tr>
          </thead>
          <tbody>
            {merchantTypes.map((rowData, i) => (
              <tr key={i}>
                <td className="px-4 py-2 border">
                  <Checkbox
                    value={rowData.id}
                    inline
                    onChange={handleCheck}
                    checked={checkedKeys.some((item) => item === rowData.id)}
                  />
                  {i + 1}
                </td>
                <td className="px-4 py-2 border">{rowData.name}</td>
                <td className="px-4 py-2 border">{rowData.description}</td>
                <td className="px-4 py-2 border">
                  {rowData.icon_url && (
                    <div className="p-2 flex justify-center items-center w-16 h-16">
                      <img src={rowData.icon_url} alt="icon_url" />
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 border">
                  <BsTrash3
                    className="text-red-500 hover:text-red-800"
                    size={16}
                    onClick={() => removeMerchantType(rowData)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!pagination?.last && (
        <div className="flex justify-center my-2">
          <Button
            onClick={() => {
              setPage((pagination?.page ?? 0) + 1);
            }}
          >
            Load more
          </Button>
        </div>
      )}
      {/* <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={pagination?.total ?? 0}
          limitOptions={[10, 30, 50]}
          limit={pagination?.size}
          activePage={pagination?.page}
          onChangePage={setPage}
          onChangeLimit={setSize}
        />
      </div> */}
      <MerchantTypeForm
        merchantType={selectedMerchantType}
        open={modalFormOpen}
        onClose={() => {
          setModalFormOpen(false);
          setSelectedMerchantType(undefined);
        }}
        onSubmit={saveMerchantType}
      />
    </MainLayout>
  );
};
export default MerchantTypePage;
